import { useState } from 'react';
import { Link } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { API_BASE } from '../lib/apiBase';
import { VersionBadge } from './VersionBadge';

type FooterLink = { label: string; to?: string };

export function Footer() {
  const { darkMode } = useDarkMode();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  async function handleNewsletterSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) throw new Error('Fehler');
      setStatus('done');
      setEmail('');
    } catch {
      setStatus('error');
    }
  }

  const columnData: { title: string; links: FooterLink[] }[] = [
    {
      title: 'Hilfe & Support',
      links: [
        { label: 'Preise', to: '/preise#preise' },
        { label: 'Berater werden', to: '/bafa-berater-werden' },
      ],
    },
    {
      title: 'Unternehmen',
      links: [{ label: 'Kontakt', to: '/kontakt' }],
    },
    {
      title: 'Rechtliches',
      links: [
        { label: 'Impressum', to: '/impressum' },
        { label: 'Datenschutz', to: '/datenschutz' },
        { label: 'AGB', to: '/agb' },
      ],
    },
  ];

  return (
    <footer
      className={`w-full border-t ${
        darkMode
          ? 'bg-slate-950 border-slate-800 text-slate-300'
          : 'bg-slate-100 border-slate-200 text-slate-600'
      }`}
    >
      <div className="px-4 md:px-8 py-12 md:py-16">
        {/* Columns Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:[grid-template-columns:repeat(4,1fr)_2fr] gap-8 lg:gap-12">
          {/* Lösungen-Platzhalter — versteckt, hält Grid-Layout */}
          <div aria-hidden="true" />

          {/* Link Columns */}
          {columnData.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <h3 className={`text-sm font-semibold tracking-wide uppercase mb-4 ${
                darkMode ? 'text-white' : 'text-slate-900'
              }`}>
                {col.title}
              </h3>
              <ul className="space-y-3">
                {col.links.map((link) => {
                  const linkClass = `text-sm transition-colors ${
                    darkMode
                      ? 'text-slate-400 hover:text-white'
                      : 'text-slate-500 hover:text-slate-900'
                  }`;
                  return (
                    <li key={link.label}>
                      {link.to ? (
                        link.to.includes('#') ? (
                          <a href={link.to} className={linkClass}>
                            {link.label}
                          </a>
                        ) : (
                          <Link to={link.to} className={linkClass}>
                            {link.label}
                          </Link>
                        )
                      ) : (
                        <a href="#" className={linkClass}>
                          {link.label}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          ))}

          {/* Newsletter Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <h3 className={`text-sm font-semibold tracking-wide uppercase mb-4 ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}>
              Abonniere unseren Newsletter
            </h3>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Die neuesten Nachrichten, Artikel und Ressourcen, wöchentlich in deinem Posteingang.
            </p>
            {status === 'done' ? (
              <p className="text-sm text-emerald-500 font-medium">
                ✓ Angemeldet! Wir melden uns bald.
              </p>
            ) : (
              <form className="flex flex-col sm:flex-row gap-2" onSubmit={handleNewsletterSubmit}>
                <label className="sr-only" htmlFor="footer-email">E-Mail-Adresse</label>
                <input
                  id="footer-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Deine E-Mail-Adresse"
                  className={`flex-1 min-w-0 px-3.5 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    darkMode
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500'
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
                  }`}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white transition-colors whitespace-nowrap"
                >
                  {status === 'loading' ? '...' : 'Abonnieren'}
                </button>
              </form>
            )}
            {status === 'error' && (
              <p className="text-xs text-red-500 mt-2">Fehler — bitte erneut versuchen.</p>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div className={`mt-12 pt-8 border-t flex flex-wrap items-center gap-x-3 gap-y-1 ${darkMode ? 'border-slate-800' : 'border-slate-300'}`}>
          <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            &copy; 2026. All rights reserved.
          </p>
          <span className={`text-sm ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>·</span>
          <VersionBadge className={darkMode ? 'text-slate-500' : 'text-slate-400'} />
        </div>
      </div>
    </footer>
  );
}
